"use client";

import { FiltersMenu } from "@components/filters-menu/filters-menu";
import { ProductList } from "@components/product-list/product-list";
import { useParams, useSearchParams } from "next/navigation";
import Chip from "@repo/ui/components/ui/chip";
import { BreadcrumbManaged } from "@repo/ui/components/ui/breadcrumb";
import Link from "next/link";
import { Category } from "@/types/homepage";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@repo/ui/components/ui/pagination";
import Icon from "@repo/ui/components/ui/icon/icon";
import { useTranslations } from "next-intl";
import { useBase64QueryState } from "@repo/next-shared/utils/nuqs-custom-parsers";
import { useCategoryPageData } from "@/data/CategoryPage";
import { Skeleton } from "@repo/ui/components/ui/skeleton";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/components/ui/button";
import { sanifyProductTitle } from "@repo/next-shared/utils";
import FiltersSidebar from "@components/filters-sidebar/filters-sidebar";

export default function CategoryComponent() {
  const t = useTranslations("CategoryPage");

  const path: string[] = useParams().categoryId as string[];
  const searchParams = useSearchParams();
  let categoryId = path[path.length - 2] || "";

  const [filters, setFilters] = useBase64QueryState(null, {
    history: "push",
    shallow: false,
  });

  const queryFilter = searchParams.get("d")?.toString() || "";

  const {
    categories,
    products,
    mostViewed,
    isLoadingProducts,
    isLoadingCategory,
  } = useCategoryPageData(categoryId, queryFilter);

  const pageNumber = filters?.pageNumber ?? "0";

  const breadcrumbItems = useMemo(() => {
    const categoryPath = categories?.categoryPath;

    if (categoryPath) {
      return [
        {
          text: t("breadcrumb-home-link"),
          href: "/",
        },
        ...(categoryPath &&
          categoryPath.map((cat: any) => ({
            text: cat.title,
            href: `/categories/${cat.path}/${sanifyProductTitle(cat.title)}`,
          }))),
      ];
    }
    return [
      {
        text: t("breadcrumb-home-link"),
        href: "/",
      },
    ];
  }, [categories]);

  const categoryPath = categories?.categoryPath ? categories?.categoryPath : [];

  const categoriesContainerRef = useRef<HTMLDivElement>(null);
  const [moreCategories, setMoreCategories] = useState(false);
  const [showMoreCategories, setShowMoreCategories] = useState(false);

  useEffect(() => {
    if (categoriesContainerRef.current) {
      const containerWidth = categoriesContainerRef.current.scrollWidth;
      const children = categories?.category?.children;
      const availableWidth =
        categoriesContainerRef.current.getBoundingClientRect().width;
      const hasChildren = children && children.length > 1;
      const shouldShow = containerWidth > availableWidth && hasChildren;
      setShowMoreCategories(!!shouldShow);
    }
  }, [categories]);

  return (
    <div className="w-full">
      {isLoadingCategory ? (
        <Skeleton className="h-[12px] w-[150px]" />
      ) : (
        <BreadcrumbManaged items={breadcrumbItems} />
      )}

      {isLoadingCategory ? (
        <Skeleton className="mt-10 h-[60px] w-[350px]"></Skeleton>
      ) : (
        <h1 className="mt-10 max-md:text-[40px]">
          {categories?.category?.title}
        </h1>
      )}

      {isLoadingCategory ? (
        <>
          <div className="my-10 flex gap-5">
            <Skeleton className="h-[50px] w-[150px] rounded-full"></Skeleton>
            <Skeleton className="h-[50px] w-[150px] rounded-full"></Skeleton>
            <Skeleton className="h-[50px] w-[150px] rounded-full"></Skeleton>
            <Skeleton className="h-[50px] w-[150px] rounded-full"></Skeleton>
            <Skeleton className="h-[50px] w-[150px] rounded-full"></Skeleton>
          </div>
          <Skeleton className="h-6 w-20" />
        </>
      ) : categories?.category?.children?.length ? (
        <div
          ref={categoriesContainerRef}
          className={cn(
            "relative mt-10 pb-5 flex gap-5 max-md:overflow-x-auto border-b border-primary-black dark:border-primary-white",
            { "md:overflow-x-hidden": showMoreCategories },
            { "md:flex-wrap": moreCategories },
          )}
        >
          {categories?.category?.children.map((child: Category) => {
            if (categoryPath) {
              return (
                <Link
                  key={child.id}
                  href={`/categories/${categoryPath[categoryPath.length - 1]?.path}/${child?.id}/${sanifyProductTitle(child?.title)}`}
                >
                  <Chip icon="Arrow">{child.title}</Chip>
                </Link>
              );
            }
          })}
          {showMoreCategories && (
            <div
              className={cn(
                "absolute right-0 top-0 max-md:hidden pl-12",
                {
                  "rounded-s-3xl bg-gradient-to-r from-white/60 to-white/100 dark:from-black/60 dark:to-black/100":
                    !moreCategories,
                },
                { "static pl-0": moreCategories },
              )}
            >
              <Button
                className="h-[50px]"
                onClick={() => setMoreCategories(!moreCategories)}
              >
                {moreCategories ? t("show-less") : t("show-more")}
              </Button>
            </div>
          )}
        </div>
      ) : null}
      {isLoadingProducts ? (
        <>
          <div className="mt-8 grid grid-cols-6 max-md:grid-cols-1 gap-4">
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
            <Skeleton className="rounded-[20px] block aspect-[10/12] max-md:aspect-[37/50] overflow-hidden shadow-catalog-image dark:shadow-none" />
          </div>
        </>
      ) : (
        products && (
          <>
            <div className="mt-8">
              <ProductList
                products={products.list}
                bestOf={mostViewed}
                totalProducts={products.totalItems}
                category={categories?.category}
              />
            </div>
            <Pagination
              className={cn("col-span-2 mt-[40px] max-md:col-span-1", {
                hidden: products.totalItems === 0,
              })}
            >
              <PaginationContent>
                <PaginationItem
                  style={{ display: parseInt(pageNumber) === 0 ? "none" : "" }}
                >
                  <Icon
                    onClick={() => {
                      setFilters({
                        ...filters,
                        pageNumber: parseInt(pageNumber) - 1,
                      });
                      window.scrollTo(0, 0);
                    }}
                    className="cursor-pointer w-[20px] h-[20px] stroke-primary-magenta dark:stroke-primary-white rotate-180"
                    icon="Arrow"
                  />
                </PaginationItem>
                <PaginationItem>
                  {t("pagination", {
                    start: parseInt(pageNumber) + 1,
                    end: products?.totalPages,
                  })}
                </PaginationItem>
                <PaginationItem
                  style={{
                    display:
                      parseInt(pageNumber) < products?.totalPages - 1
                        ? ""
                        : "none",
                  }}
                >
                  <Icon
                    onClick={() => {
                      setFilters({
                        ...filters,
                        pageNumber: parseInt(pageNumber) + 1,
                      });
                      window.scrollTo(0, 0);
                    }}
                    className="cursor-pointer w-[20px] h-[20px] stroke-primary-magenta dark:stroke-primary-white"
                    icon="Arrow"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </>
        )
      )}
    </div>
  );
}
